import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Package, ArrowLeft, Search, RefreshCw,
  DollarSign, AlertTriangle, XCircle, ChevronDown, ChevronUp,
  FileSpreadsheet, Printer,
} from 'lucide-react';
import Layout from '../../../components/Layout';
import {
  inventoryReportService,
  InventoryStockSummaryDTO,
} from '../../../services/inventoryReportService';
import { exportToCsv, printReport } from '../../../utils/exportUtils';
import './InventoryReportCommon.css';

const fmt   = (n: number) => new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtQty = (n: number) => new Intl.NumberFormat().format(n);

const StockSummaryReportPage: React.FC = () => {
  const navigate  = useNavigate();
  const { t }     = useTranslation();

  const [warehouseId, setWarehouseId] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [searching,   setSearching]   = useState(false);
  const [data,        setData]        = useState<InventoryStockSummaryDTO | null>(null);
  const [expanded,    setExpanded]    = useState<Set<number>>(new Set());

  const handleSearch = async () => {
    setSearching(true);
    setHasSearched(true);
    setExpanded(new Set());
    try {
      const res = await inventoryReportService.getStockSummary({
        warehouseId: warehouseId ? Number(warehouseId) : undefined,
      });
      const d: InventoryStockSummaryDTO = (res.data as any)?.data ?? res.data;
      setData(d);
    } catch { setData(null); }
    finally { setSearching(false); }
  };

  const toggle = (id: number) =>
    setExpanded(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });

  const handleExcelExport = () => {
    if (!data) return;
    exportToCsv(
      `stock-summary-${new Date().toISOString().split('T')[0]}.csv`,
      ['SKU', 'Product', 'Category', 'Total Qty', 'Reserved', 'Available', 'Reorder Level', 'Unit Cost', 'Total Value', 'Status'],
      data.items.map(i => [
        i.sku, i.productName, i.categoryName ?? '', i.totalQuantity, i.reservedQuantity,
        i.availableQuantity, i.reorderLevel, i.unitCost, i.totalValue,
        i.isOutOfStock ? 'Out of Stock' : i.isLowStock ? 'Low Stock' : 'OK',
      ]),
    );
  };

  const items = data?.items ?? [];

  return (
    <Layout>
      <div className="irp-page">

        {/* Hero */}
        <div className="irp-hero">
          <div>
            <h1 className="irp-hero-title"><Package size={22} strokeWidth={1.8} />
              {t('inventoryReports.stockSummaryTitle', 'Stock Summary')}
            </h1>
            <p className="irp-hero-sub">
              {t('inventoryReports.stockSummarySubtitle', 'Current stock levels for all products')}
            </p>
          </div>
          <div className="irp-hero-actions">
            {hasSearched && items.length > 0 && (
              <div className="irp-export-group">
                <button className="irp-export-btn irp-export-excel" onClick={handleExcelExport}>
                  <FileSpreadsheet size={13} /> Export Excel
                </button>
                <button className="irp-export-btn irp-export-pdf" onClick={printReport}>
                  <Printer size={13} /> Print / PDF
                </button>
              </div>
            )}
            <button className="irp-back-btn" onClick={() => navigate('/reports/inventory')}>
              <ArrowLeft size={14} /> {t('common.back', 'Back')}
            </button>
          </div>
        </div>

        {/* Filter */}
        <div className="ims-card">
          <div className="ims-card-header">
            <span className="ims-card-title"><Search size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />Filters</span>
          </div>
          <div className="ims-card-body" style={{ padding: '1rem 1.25rem' }}>
            <div className="irp-filter-bar">
              <div className="irp-filter-group">
                <span className="irp-filter-label">Warehouse ID (optional)</span>
                <input
                  type="number"
                  className="ims-form-control"
                  placeholder="All warehouses"
                  value={warehouseId}
                  onChange={e => setWarehouseId(e.target.value)}
                />
              </div>
              <button
                className="ims-btn ims-btn-primary"
                onClick={handleSearch}
                disabled={searching}
                style={{ alignSelf: 'flex-end' }}
              >
                {searching
                  ? <><span className="irp-spinner" /> Searching…</>
                  : <><RefreshCw size={14} /> Load Report</>}
              </button>
            </div>
          </div>
        </div>

        {/* Prompt */}
        {!hasSearched && (
          <div className="irp-prompt">
            <Package size={48} strokeWidth={1} />
            <p>Click <strong>Load Report</strong> to view current stock levels.</p>
          </div>
        )}

        {/* Results */}
        {hasSearched && !searching && data && (
          <>
            {/* KPI cards */}
            <div className="ims-stats-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-total"><Package size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value">{fmtQty(data.totalProducts)}</span>
                  <span className="ims-stat-label">Total Products</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-value"><DollarSign size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>{fmt(data.totalStockValue)}</span>
                  <span className="ims-stat-label">Total Stock Value</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-low"><AlertTriangle size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value">{fmtQty(data.lowStockCount)}</span>
                  <span className="ims-stat-label">Low Stock Items</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-out"><XCircle size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value">{fmtQty(data.outOfStockCount)}</span>
                  <span className="ims-stat-label">Out of Stock</span>
                </div>
              </div>
            </div>

            {/* Table */}
            {items.length === 0 ? (
              <div className="ims-card"><div className="irp-empty">No stock records found.</div></div>
            ) : (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <Package size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    Stock Records
                    <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--ims-text-muted)', marginInlineStart: 6 }}>
                      ({items.length} products)
                    </span>
                  </span>
                </div>
                <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="irp-table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Product</th>
                        <th>Category</th>
                        <th className="num">Total Qty</th>
                        <th className="num">Reserved</th>
                        <th className="num">Available</th>
                        <th className="num">Reorder Lvl</th>
                        <th className="num">Unit Cost</th>
                        <th className="num">Total Value</th>
                        <th>Status</th>
                        <th>WH</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => (
                        <React.Fragment key={item.productId}>
                          <tr>
                            <td><span className="irp-sku">{item.sku}</span></td>
                            <td style={{ fontWeight: 500 }}>{item.productName}</td>
                            <td>{item.categoryName ?? '—'}</td>
                            <td className="num">{fmtQty(item.totalQuantity)}</td>
                            <td className="num">{fmtQty(item.reservedQuantity)}</td>
                            <td className="num">{fmtQty(item.availableQuantity)}</td>
                            <td className="num">{fmtQty(item.reorderLevel)}</td>
                            <td className="num">{fmt(item.unitCost)}</td>
                            <td className="num irp-value">{fmt(item.totalValue)}</td>
                            <td>
                              {item.isOutOfStock
                                ? <span className="ims-badge ims-badge-rejected">Out of Stock</span>
                                : item.isLowStock
                                  ? <span className="ims-badge ims-badge-pending">Low Stock</span>
                                  : <span className="ims-badge ims-badge-approved">OK</span>}
                            </td>
                            <td>
                              {item.byWarehouse.length > 1 && (
                                <button className="irp-btn-expand" onClick={() => toggle(item.productId)}>
                                  {expanded.has(item.productId)
                                    ? <><ChevronUp size={11} /> Hide</>
                                    : <><ChevronDown size={11} /> {item.byWarehouse.length}</>}
                                </button>
                              )}
                            </td>
                          </tr>
                          {expanded.has(item.productId) && (
                            <tr className="irp-detail-row">
                              <td colSpan={11}>
                                <div className="irp-detail-inner">
                                  <h4 className="irp-detail-title">By Warehouse — {item.productName}</h4>
                                  <table className="irp-sub-table">
                                    <thead>
                                      <tr>
                                        <th>Warehouse</th>
                                        <th className="num">Qty</th>
                                        <th className="num">Reserved</th>
                                        <th className="num">Available</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {item.byWarehouse.map(wh => (
                                        <tr key={wh.warehouseId}>
                                          <td>{wh.warehouseName}</td>
                                          <td className="num">{fmtQty(wh.quantity)}</td>
                                          <td className="num">{fmtQty(wh.reservedQuantity)}</td>
                                          <td className="num">{fmtQty(wh.availableQuantity)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3} style={{ fontWeight: 700 }}>TOTAL</td>
                        <td className="num">{fmtQty(items.reduce((s, i) => s + i.totalQuantity, 0))}</td>
                        <td className="num">{fmtQty(items.reduce((s, i) => s + i.reservedQuantity, 0))}</td>
                        <td className="num">{fmtQty(items.reduce((s, i) => s + i.availableQuantity, 0))}</td>
                        <td colSpan={2}></td>
                        <td className="num">{fmt(data.totalStockValue)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Breakdown */}
            {items.length > 0 && (
              <>
                <span className="irp-section-title">Stock Status Breakdown</span>
                <div className="irp-breakdown-grid">
                  <div className="irp-breakdown-card irp-breakdown-indigo">
                    <div className="irp-breakdown-label">Total Products</div>
                    <div className="irp-breakdown-value">{data.totalProducts}</div>
                    <div className="irp-breakdown-sub">in system</div>
                  </div>
                  <div className="irp-breakdown-card irp-breakdown-green">
                    <div className="irp-breakdown-label">Healthy Stock</div>
                    <div className="irp-breakdown-value">{data.totalProducts - data.lowStockCount - data.outOfStockCount}</div>
                    <div className="irp-breakdown-sub">
                      {data.totalProducts > 0
                        ? (((data.totalProducts - data.lowStockCount - data.outOfStockCount) / data.totalProducts) * 100).toFixed(1) + '%'
                        : '0%'}
                    </div>
                  </div>
                  <div className="irp-breakdown-card irp-breakdown-amber">
                    <div className="irp-breakdown-label">Low Stock</div>
                    <div className="irp-breakdown-value">{data.lowStockCount}</div>
                    <div className="irp-breakdown-sub">
                      {data.totalProducts > 0
                        ? ((data.lowStockCount / data.totalProducts) * 100).toFixed(1) + '%'
                        : '0%'}
                    </div>
                  </div>
                  <div className="irp-breakdown-card irp-breakdown-red">
                    <div className="irp-breakdown-label">Out of Stock</div>
                    <div className="irp-breakdown-value">{data.outOfStockCount}</div>
                    <div className="irp-breakdown-sub">
                      {data.totalProducts > 0
                        ? ((data.outOfStockCount / data.totalProducts) * 100).toFixed(1) + '%'
                        : '0%'}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

      </div>
    </Layout>
  );
};

export default StockSummaryReportPage;
